<template>
    <div>
        <div class="min-h-screen bg-white overflow-hidden flex">
            <TransitionRoot as="template" :show="sidebarOpen">
                <Dialog
                    as="div"
                    static
                    class="fixed inset-0 z-40 flex md:hidden"
                    @close="sidebarOpen = false"
                    :open="sidebarOpen"
                >
                    <TransitionChild
                        as="template"
                        enter="transition-opacity ease-linear duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-600 bg-opacity-75"
                        />
                    </TransitionChild>
                    <TransitionChild
                        as="template"
                        enter="transition ease-in-out duration-300 transform"
                        enter-from="-translate-x-full"
                        enter-to="translate-x-0"
                        leave="transition ease-in-out duration-300 transform"
                        leave-from="translate-x-0"
                        leave-to="-translate-x-full"
                    >
                        <div
                            class="relative max-w-xs w-full bg-white pt-5 pb-4 flex-1 flex flex-col"
                        >
                            <TransitionChild
                                as="template"
                                enter="ease-in-out duration-300"
                                enter-from="opacity-0"
                                enter-to="opacity-100"
                                leave="ease-in-out duration-300"
                                leave-from="opacity-100"
                                leave-to="opacity-0"
                            >
                                <div class="absolute top-0 right-0 -mr-14 p-1">
                                    <button
                                        class="h-12 w-12 rounded-full flex items-center justify-center focus:outline-none focus:bg-gray-600"
                                        @click="sidebarOpen = false"
                                    >
                                        <XIcon
                                            class="h-6 w-6 text-white"
                                            aria-hidden="true"
                                        />
                                        <span class="sr-only"
                                            >Close sidebar</span
                                        >
                                    </button>
                                </div>
                            </TransitionChild>
                            <div class="flex-shrink-0 px-4 flex items-center">
                                <logo-nl
                                    height="h-24  text-white mx-auto"
                                    class="h-24 text-white mx-auto"
                                ></logo-nl>
                            </div>
                            <div class="mt-5 flex-1 h-0 overflow-y-auto">
                                <nav class="h-full flex flex-col">
                                    <div class="space-y-1">
                                        <router-link
                                            v-for="item in navigation"
                                            :key="item.name"
                                            :to="{ name: item.href }"
                                            :class="[
                                                checkCurrent(item.href)
                                                    ? 'bg-blue-100 border-blue-600 text-blue-600'
                                                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                                                'group border-l-4 py-2 px-3 flex items-center text-base font-medium',
                                            ]"
                                            :aria-current="
                                                checkCurrent(item.href)
                                                    ? 'page'
                                                    : undefined
                                            "
                                        >
                                            <component
                                                :is="item.icon"
                                                :class="[
                                                    checkCurrent(item.href)
                                                        ? 'text-blue-500'
                                                        : 'text-gray-400 group-hover:text-gray-500',
                                                    'mr-4 h-6 w-6',
                                                ]"
                                                aria-hidden="true"
                                            />
                                            {{ item.name }}
                                        </router-link>
                                    </div>
                                </nav>
                            </div>
                        </div>
                    </TransitionChild>
                    <div class="flex-shrink-0 w-14" aria-hidden="true">
                        <!-- Dummy element to force sidebar to shrink to fit close icon -->
                    </div>
                </Dialog>
            </TransitionRoot>

            <!-- Static sidebar for desktop -->
            <div class="hidden md:flex md:flex-shrink-0 shadow-2xl">
                <div class="w-64 flex flex-col">
                    <!-- Sidebar component, swap this element with another sidebar if you like -->
                    <nav
                        class="bg-gray-50 border-r border-gray-200 pt-5 pb-4 flex flex-col flex-grow overflow-y-auto"
                    >
                        <div class="flex-shrink-0 px-4 flex items-center">
                            <logo-nl
                                class="h-36 text-white mx-auto"
                                height="h-36  text-white mx-auto"
                                color="rgba(75, 85, 99, var(--tw-text-opacity))"
                            ></logo-nl>
                        </div>
                        <div class="flex-grow mt-5 flex flex-col">
                            <div class="flex-1 space-y-1">
                                <router-link
                                    v-for="item in navigation"
                                    :key="item.name"
                                    :to="{ name: item.href }"
                                    :class="[
                                        checkCurrent(item.href)
                                            ? 'bg-blue-500 border-blue-700 text-white'
                                            : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50',
                                        'group border-l-4 py-2 px-3 flex items-center text-sm font-medium',
                                    ]"
                                >
                                    <component
                                        :is="item.icon"
                                        :class="[
                                            checkCurrent(item.href)
                                                ? 'text-blue-50'
                                                : 'text-gray-400 group-hover:text-gray-500',
                                            'mr-3 h-6 w-6',
                                        ]"
                                        aria-hidden="true"
                                    />
                                    {{ item.name }}
                                </router-link>
                            </div>
                        </div>
                    </nav>
                </div>
            </div>

            <router-view
                v-slot="{ Component }"
                name="details"
                class="bg-blue-50 px-4 lg:px-0"
            >
                <transition name="fade" mode="out-in">
                    <component :is="Component"></component>
                </transition>
            </router-view>
            <!-- Content area -->
        </div>
    </div>
</template>
<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-active {
    opacity: 0;
}
</style>
<script>
import {
    Dialog,
    DialogOverlay,
    Switch,
    SwitchGroup,
    SwitchLabel,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {
    BellIcon,
    CalendarIcon,
    ChatIcon,
    CogIcon,
    UserGroupIcon,
    HomeIcon,
    MenuAlt2Icon,
    QuestionMarkCircleIcon,
    MailIcon,
    XIcon,
} from "@heroicons/vue/outline";
import { SearchIcon } from "@heroicons/vue/solid";

// const navigation = [
//     { name: "Dashboard", href: "dashboard", icon: HomeIcon },
//     { name: "Vergaderingen", href: "meetings", icon: CalendarIcon },
//     { name: "Deelnemers", href: "attendees", icon: UserGroupIcon },
//     // { name: "Messages", href: "home", icon: ChatIcon },
//     // { name: "Team", href: "home", icon: UsersIcon },
//     { name: "Instellingen", href: "profile", icon: CogIcon },
// ];
export default {
    mounted() {
        Eventing.listen("openSidebar", () => (this.sidebarOpen = true));
        // alert("HEY");

        this.navigation = [
            {
                name: this.t("navigation.mailings", "Mailings"),
                href: "mailings",
                icon: MailIcon,
            },
        ];
    },
    components: {
        Dialog,
        DialogOverlay,
        Switch,
        SwitchGroup,
        SwitchLabel,
        TransitionChild,
        TransitionRoot,
        BellIcon,
        MenuAlt2Icon,
        SearchIcon,
        XIcon,
    },
    created() {},
    props: [],
    data() {
        return {
            sidebarOpen: false,
            automaticTimezoneEnabled: true,
            autoUpdateApplicantDataEnabled: false,
            navigation: {},
        };
    },
    methods: {
        checkCurrent(route) {
            return this.$route.name.split("-")[0].includes(route.split("-")[0])
                ? true
                : false;
        },
    },
};
</script>
